const CLASS_HIT_DIE = Object.freeze({
  barbarian: 12,
  fighter: 10,
  paladin: 10,
  ranger: 10,
  artificer: 8,
  bard: 8,
  cleric: 8,
  druid: 8,
  monk: 8,
  rogue: 8,
  warlock: 8,
  sorcerer: 6,
  wizard: 6
});

const asArray = (value) => (Array.isArray(value) ? value : []);

const asRecord = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const finiteNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizedName = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const collectCharacterBranches = (character) => [
  character,
  character?.data,
  character?.schemaData,
  character?.canonicalData,
  character?.characterData,
  character?.characterData?.data,
  character?.compat?.originalData
].filter((branch) => branch && typeof branch === 'object');

export const resolveCharacterTotalLevel = (character) => {
  const classes = collectCharacterBranches(character)
    .map((branch) => branch.classes)
    .find((value) => Array.isArray(value) && value.length > 0);
  if (classes) {
    const total = classes.reduce((sum, entry) => sum + Math.max(0, finiteNumber(entry?.level, 0)), 0);
    if (total > 0) return total;
  }
  for (const branch of collectCharacterBranches(character)) {
    const level = finiteNumber(branch.level);
    if (level != null && level > 0) return level;
  }
  return 1;
};

const hitDieSizeFromText = (value) => {
  const match = String(value || '').trim().match(/(?:^|\+)\s*(?:\d+)?d(4|6|8|10|12|20)\b/i);
  return match ? Number(match[1]) : null;
};

const resolveClassList = (character) => {
  const classList = collectCharacterBranches(character)
    .map((branch) => branch.classes)
    .find((value) => Array.isArray(value) && value.length > 0);
  if (classList) return classList;
  const className = collectCharacterBranches(character)
    .map((branch) => branch.class || branch.characterClass)
    .find(Boolean);
  return className ? [{ name: className, level: resolveCharacterTotalLevel(character) }] : [];
};

const derivedHitDiceFormula = (character) => {
  const diceBySize = new Map();
  resolveClassList(character).forEach((entry) => {
    const name = normalizedName(entry?.name || entry?.className || entry?.class);
    const size = hitDieSizeFromText(entry?.hitDice || entry?.hitDie)
      || CLASS_HIT_DIE[name];
    const level = Math.max(0, Math.floor(finiteNumber(entry?.level, 0)));
    if (size && level > 0) diceBySize.set(size, (diceBySize.get(size) || 0) + level);
  });
  return Array.from(diceBySize.entries())
    .sort(([left], [right]) => right - left)
    .map(([size, count]) => `${count}d${size}`)
    .join(' + ');
};

const explicitHitDiceFormula = (character) => {
  for (const branch of collectCharacterBranches(character)) {
    const hitDice = asRecord(branch.hitDice);
    const raw = String(hitDice?.total ?? branch.hitDice ?? '').trim();
    if (/^\d+d(?:4|6|8|10|12|20)(?:\s*\+\s*\d+d(?:4|6|8|10|12|20))*$/i.test(raw)) {
      return raw.replace(/\s*\+\s*/g, ' + ').toLowerCase();
    }
  }
  return '';
};

const countHitDice = (formula) => Array.from(String(formula || '').matchAll(/(\d+)d\d+/gi))
  .reduce((sum, match) => sum + Number(match[1]), 0);

/**
 * Returns both the rule (total dice) and the mutable state (spent/remaining).
 * A legacy per-class value such as 1d8 never suppresses a level-derived 5d8 total.
 */
export const resolveCharacterHitDice = (character) => {
  const derived = derivedHitDiceFormula(character);
  const explicit = explicitHitDiceFormula(character);
  // Class progression is the authoritative source whenever it includes a known class
  // or an explicit hit die. A stale single-class total can have the correct number of
  // dice but the wrong die sizes after multiclassing. date_of_change: 2026-08-08.
  const formula = derived || explicit;
  const totalCount = countHitDice(formula);
  let spent = 0;
  for (const branch of collectCharacterBranches(character)) {
    const hitDice = asRecord(branch.hitDice);
    const candidate = finiteNumber(branch.hitDiceSpent ?? hitDice?.spent);
    if (candidate != null) {
      spent = candidate;
      break;
    }
  }
  spent = Math.max(0, Math.min(totalCount, Math.floor(spent)));
  const remaining = Math.max(0, totalCount - spent);
  return {
    formula,
    totalCount,
    spent,
    remaining,
    label: formula ? `${formula} - ${remaining} remaining` : '',
    shortRestLabel: formula ? `${remaining} of ${totalCount} available to spend on a Short Rest` : ''
  };
};

export const collectCharacterEquipment = (character) => {
  const results = [];
  const seen = new Set();
  const push = (entry) => {
    if (!entry) return;
    const item = typeof entry === 'string' ? { name: entry } : entry;
    const name = String(item?.name || item?.title || item?.label || '').trim();
    if (!name) return;
    const fingerprint = `${normalizedName(name)}|${item?.id || ''}`;
    if (seen.has(fingerprint)) return;
    seen.add(fingerprint);
    results.push(item);
  };
  collectCharacterBranches(character).forEach((branch) => {
    const equipment = asRecord(branch.equipment);
    const detailed = asRecord(branch.equipmentDetailed);
    const inventory = asRecord(branch.inventory);
    const inventoryEquipment = asRecord(inventory?.equipment);
    [
      equipment,
      detailed,
      inventoryEquipment
    ].filter(Boolean).forEach((container) => {
      ['items', 'weapons', 'armor', 'tools', 'other', 'magicItems'].forEach((key) => {
        asArray(container[key]).forEach(push);
      });
    });
    [branch.items, branch.magicItems, branch.weapons, branch.equippedWeapons, branch.equippedArmor, inventory?.equippedWeapons]
      .forEach((collection) => asArray(collection).forEach(push));
    if (Array.isArray(branch.equipment)) branch.equipment.forEach(push);
  });
  return results;
};

const dexterityModifier = (character) => {
  for (const branch of collectCharacterBranches(character)) {
    const score = finiteNumber(branch?.abilities?.dexterity ?? branch?.abilities?.dex);
    if (score != null) return Math.floor((score - 10) / 2);
  }
  return 0;
};

export const resolveCharacterArmorClass = (character) => {
  const explicit = collectCharacterBranches(character)
    .map((branch) => finiteNumber(branch.ac?.value ?? branch.ac ?? branch.armorClass ?? branch.combatStats?.armorClass))
    .find((value) => value != null);
  const armor = collectCharacterEquipment(character).find((item) => {
    const name = normalizedName(item?.name);
    return item?.equipped === true && /glamou?red studded leather/.test(name);
  });
  if (!armor) return { value: explicit ?? 10, source: explicit != null ? 'Recorded Armor Class' : 'Unarmored' };

  const cosmeticOnly = armor.cosmeticOnly === true
    || armor.mechanical === false
    || armor.variant === 'cosmetic-only'
    || finiteNumber(armor.magicBonus ?? armor.armorClassBonus) === 0;
  const magicalBonus = cosmeticOnly ? 0 : (finiteNumber(armor.magicBonus ?? armor.armorClassBonus, 1));
  const value = 12 + dexterityModifier(character) + magicalBonus;
  return {
    value,
    source: `${cosmeticOnly ? 'Custom cosmetic-only' : 'Glamoured'} Studded Leather (12 + Dex${magicalBonus ? ` + ${magicalBonus}` : ''})`
  };
};

export const resolveCharacterDefensesAndSenses = (character) => {
  const defenses = [];
  const senses = [];
  const addUnique = (list, value) => {
    if (value && !list.some((entry) => normalizedName(entry) === normalizedName(value))) list.push(value);
  };

  collectCharacterBranches(character).forEach((branch) => {
    asArray(branch.defenses).forEach((entry) => addUnique(defenses, typeof entry === 'string' ? entry : entry?.name || entry?.description));
    asArray(branch.senses).forEach((entry) => addUnique(senses, typeof entry === 'string' ? entry : entry?.name || entry?.description));
  });
  collectCharacterEquipment(character).forEach((item) => {
    const name = normalizedName(item?.name);
    if (name.includes('brooch of shielding')) {
      addUnique(defenses, 'Resistance to Force damage');
      addUnique(defenses, 'Immune to Magic Missile');
    }
    if (name.includes('goggles of night')) addUnique(senses, 'Darkvision 60 ft.');
  });
  return { defenses, senses };
};

const ITEM_ACTION_REFERENCES = Object.freeze([
  { pattern: /glamou?red studded leather/i, name: 'Change Glamoured Armor Appearance', activationType: 'bonus action', description: 'Change the armor to look like another suit of armor or normal clothing. Its properties do not change.' },
  { pattern: /manticore statuette/i, name: 'Activate Manticore Statuette', activationType: 'action', description: 'Magic Action. Activate the statuette, then track its 7-day cooldown.', uses: { max: 1, resetOn: '7 days' } },
  { pattern: /whisperwire brooch/i, name: 'Whisperwire Message', activationType: 'action', description: 'Use the brooch to send its Message effect; mark a use on the item tracker.' },
  { pattern: /househand clasp/i, name: 'Househand Prestidigitation', activationType: 'action', description: 'Cast Prestidigitation from the clasp (3 uses per day).', uses: { max: 3, resetOn: 'day' } },
  { pattern: /shuttered spark beetle/i, name: 'Shuttered Spark Beetle', activationType: 'action', description: 'Activate the beetle and mark one of its 2 daily uses.', uses: { max: 2, resetOn: 'day' } },
  { pattern: /eversmoking bottle/i, name: 'Open or Close Eversmoking Bottle', activationType: 'action', description: 'Use a Magic Action to open or close the bottle.' },
  { pattern: /immovable rod/i, name: 'Activate Immovable Rod', activationType: 'action', description: 'Press the rod\'s button to fix or release it in place.' },
  { pattern: /sending stone/i, name: 'Use Sending Stone', activationType: 'action', description: 'Use a Magic Action to cast Sending from the stone; track its reset.' },
  { pattern: /potion/i, name: 'Drink Potion', activationType: 'bonus action', description: 'Drink an accessible potion and remove or mark the consumed item.', consumable: true }
]);

export const buildCharacterItemActions = (character) => {
  const seen = new Set();
  return collectCharacterEquipment(character).flatMap((item) => {
    const itemName = String(item?.name || '').trim();
    const reference = ITEM_ACTION_REFERENCES.find(({ pattern }) => pattern.test(itemName));
    if (!reference) return [];
    const key = `${reference.name}|${itemName}`.toLowerCase();
    if (seen.has(key)) return [];
    seen.add(key);
    return [{
      ...reference,
      type: 'object',
      source: itemName,
      itemData: item,
      activationType: reference.activationType,
      actionType: reference.activationType
    }];
  });
};

export const hasCharacterNamedFeature = (character, name) => {
  const target = normalizedName(name);
  return collectCharacterBranches(character).some((branch) => [
    branch.feats,
    branch.features,
    branch.classFeatures,
    branch.speciesTraits
  ].some((collection) => asArray(collection).some((entry) => normalizedName(typeof entry === 'string' ? entry : entry?.name) === target)));
};

export const resolveCharacterChoiceSummary = (character) => {
  const selections = [];
  const unresolved = [];
  const featureEntries = collectCharacterBranches(character).flatMap((branch) => [
    ...asArray(branch.feats),
    ...asArray(branch.features),
    ...asArray(branch.classFeatures)
  ]);
  ['Skillful', 'Versatile'].forEach((choiceName) => {
    const entry = featureEntries.find((candidate) => normalizedName(typeof candidate === 'string' ? candidate : candidate?.name) === normalizedName(choiceName));
    if (!entry) return;
    const choice = typeof entry === 'object' ? String(entry.selection || entry.choice || entry.selected || '').trim() : '';
    if (choice) selections.push(`${choiceName}: ${choice}`);
    else unresolved.push(`${choiceName} selection`);
  });

  const masteries = collectCharacterEquipment(character)
    .filter((item) => item?.mastery || item?.weaponMastery || item?.masteryProperty)
    .map((item) => `${item.name}: ${item.mastery || item.weaponMastery || item.masteryProperty}`);
  if (masteries.length > 0) selections.push(`Weapon Mastery: ${masteries.join('; ')}`);
  if (masteries.length === 1) unresolved.push('Second Weapon Mastery selection');

  const languages = collectCharacterBranches(character)
    .map((branch) => branch.languages || branch.proficiencies?.languages)
    .find(Array.isArray) || [];
  if (languages.length > 0 && languages.length < 3) unresolved.push('Language selection');
  return { selections, unresolved };
};

const cleanReferenceEntry = (entry) => {
  const record = typeof entry === 'string' ? { name: entry } : { ...(entry || {}) };
  const name = String(record.name || record.title || '').trim()
    .replace(/mage hand legerdemaine/gi, 'Mage Hand Legerdemain');
  const rawDescription = String(record.description || record.text || '').trim();
  const description = /^(?:no description available|no description|n\/a)$/i.test(rawDescription)
    ? ''
    : rawDescription.replace(/mage hand legerdemaine/gi, 'Mage Hand Legerdemain');
  if (!name || /starter character generator/i.test(name)) return null;
  return { ...record, name, description, text: description };
};

export const buildCharacterReferencePresentation = (character) => {
  const seen = new Set();
  const dexterity = finiteNumber(character?.abilities?.dexterity, 10);
  const proficiency = finiteNumber(character?.proficiencyBonus, Math.floor((resolveCharacterTotalLevel(character) - 1) / 4) + 2);
  const cunningStrikeDC = 8 + Math.floor((dexterity - 10) / 2) + proficiency;
  const hasPoisonersKit = collectCharacterEquipment(character)
    .some((item) => /poisoner'?s kit/i.test(String(item?.name || '')));
  const normalizeGroup = (entries) => asArray(entries).flatMap((entry) => {
    const cleaned = cleanReferenceEntry(entry);
    if (!cleaned) return [];
    const key = normalizedName(cleaned.name);
    if (seen.has(key)) return [];
    seen.add(key);
    if (key === 'cunning strike') {
      cleaned.description = `Sneak Attack becomes 2d6 when you use an option. Save DC ${cunningStrikeDC}. Poison is ${hasPoisonersKit ? 'available; repeat the save at the end of each turn' : 'unavailable without a Poisoner’s Kit'}. Withdraw moves you up to 15 ft. without provoking Opportunity Attacks.`;
      cleaned.text = cleaned.description;
    }
    return [cleaned];
  });
  return {
    classFeatures: normalizeGroup(character?.classFeatures),
    features: normalizeGroup(character?.features),
    speciesTraits: normalizeGroup(character?.speciesTraits),
    feats: normalizeGroup(character?.feats)
  };
};

export default resolveCharacterHitDice;
